import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { useH2H365 } from '@/features/matches/hooks';
import type {
  H2H365Meeting,
  H2H365RecentGame,
  H2H365Side,
  H2H365TeamForm,
} from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** A small crest badge with a graceful fallback dot when no url is known. */
function Crest({ uri, size = 16 }: { uri: string | null; size?: number }) {
  if (!uri) {
    return <View style={{ width: size, height: size }} className="rounded-full bg-white/10" />;
  }
  return (
    <Image
      source={{ uri }}
      style={{ width: size, height: size }}
      contentFit="contain"
      cachePolicy="memory-disk"
    />
  );
}

/**
 * The 3-segment W-D-L summary bar, oriented to the CURRENT fixture's home team:
 * home wins (green) · draws (grey) · away wins (red). Widths are proportional to
 * the totals; a side with zero collapses to nothing.
 */
function SummaryBar({
  homeWins,
  draws,
  awayWins,
}: {
  homeWins: number;
  draws: number;
  awayWins: number;
}) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  const total = Math.max(homeWins + draws + awayWins, 1);
  const num = (n: number) => localizeDigits(String(n), arabicNumerals);
  return (
    <View className="gap-2">
      <View className="h-2.5 flex-row overflow-hidden rounded-full bg-white/10">
        <View style={{ flex: homeWins, backgroundColor: palette.win }} />
        <View style={{ flex: draws, backgroundColor: palette.draw }} />
        <View style={{ flex: awayWins, backgroundColor: palette.loss }} />
        {/* Keep a thin track visible when every count is 0. */}
        {homeWins + draws + awayWins === 0 ? <View style={{ flex: total }} /> : null}
      </View>
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center gap-1.5">
          <View style={{ width: 8, height: 8, backgroundColor: palette.win }} className="rounded-full" />
          <AppText variant="micro" weight="bold" tabular className="text-ink">
            {num(homeWins)}
          </AppText>
        </View>
        <View className="flex-row items-center gap-1.5">
          <View style={{ width: 8, height: 8, backgroundColor: palette.draw }} className="rounded-full" />
          <AppText variant="micro" weight="bold" tabular className="text-soft">
            {num(draws)}
          </AppText>
        </View>
        <View className="flex-row items-center gap-1.5">
          <AppText variant="micro" weight="bold" tabular className="text-ink">
            {num(awayWins)}
          </AppText>
          <View style={{ width: 8, height: 8, backgroundColor: palette.loss }} className="rounded-full" />
        </View>
      </View>
    </View>
  );
}

/** One previous-meeting row: year · competition · crest+score vs crest+score. */
function MeetingRow({ meeting, isLast }: { meeting: H2H365Meeting; isLast: boolean }) {
  const { arabicNumerals, isRTL } = useLocale();
  const num = (n: number) => localizeDigits(String(n), arabicNumerals);
  const year = meeting.date ? num(new Date(meeting.date).getFullYear()) : '';
  const home = meeting.home;
  const away = meeting.away;
  const hs = home.score ?? 0;
  const as = away.score ?? 0;
  // RN-web forces Text direction:ltr, so reverse the score string in RTL and
  // wrap it in isolates so it never bleeds into surrounding text.
  const score = isRTL ? `⁦${num(as)} - ${num(hs)}⁩` : `⁦${num(hs)} - ${num(as)}⁩`;

  return (
    <View
      className={`flex-row items-center gap-2 px-4 py-2.5 ${
        isLast ? '' : 'border-b border-glass-border'
      }`}
    >
      <AppText variant="micro" tabular className="text-muted" style={{ width: 34 }}>
        {year}
      </AppText>
      {meeting.competition ? (
        <AppText variant="micro" className="flex-1 text-muted" numberOfLines={1}>
          {meeting.competition}
        </AppText>
      ) : (
        <View className="flex-1" />
      )}
      <View className="flex-row items-center gap-1.5">
        <AppText variant="micro" className="text-ink" numberOfLines={1} style={{ maxWidth: 64, textAlign: 'right' }}>
          {home.name ?? ''}
        </AppText>
        <Crest uri={(home as H2H365Side).crest} />
        <View className="rounded bg-deep/70 px-2 py-0.5">
          <AppText variant="caption" weight="bold" tabular className="text-ink">
            {score}
          </AppText>
        </View>
        <Crest uri={(away as H2H365Side).crest} />
        <AppText variant="micro" className="text-ink" numberOfLines={1} style={{ maxWidth: 64 }}>
          {away.name ?? ''}
        </AppText>
      </View>
    </View>
  );
}

/**
 * One recent-form row for a single team (365 "وجها لوجه" style): a coloured
 * W/D/L badge · opponent crest+name · the scoreline (this team's goals first) ·
 * a compact date. No betting odds — purely results.
 */
function RecentRow({ game, isLast }: { game: H2H365RecentGame; isLast: boolean }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const num = (n: number) => localizeDigits(String(n), arabicNumerals);

  const meta =
    game.result === 'W'
      ? { color: palette.win, label: t('match.formWin') }
      : game.result === 'L'
        ? { color: palette.loss, label: t('match.formLoss') }
        : game.result === 'D'
          ? { color: palette.draw, label: t('match.formDraw') }
          : { color: palette.muted, label: '–' };

  const shortDate = () => {
    if (!game.date) return '';
    const d = new Date(game.date);
    if (Number.isNaN(d.getTime())) return '';
    return `${num(d.getDate())}/${num(d.getMonth() + 1)}`;
  };
  const score = `⁦${num(game.ourScore ?? 0)} - ${num(game.oppScore ?? 0)}⁩`;

  return (
    <View
      className={`flex-row items-center gap-2.5 px-4 py-2.5 ${
        isLast ? '' : 'border-b border-glass-border'
      }`}
    >
      <View
        style={{ width: 20, height: 20, borderRadius: 10, backgroundColor: meta.color }}
        className="items-center justify-center"
      >
        <AppText variant="micro" weight="bold" style={{ color: '#0A0A18', fontSize: 10 }}>
          {meta.label}
        </AppText>
      </View>
      <Crest uri={game.opponent.crest} />
      <AppText variant="micro" className="flex-1 text-ink" numberOfLines={1}>
        {game.opponent.name ?? ''}
      </AppText>
      <View className="rounded bg-deep/70 px-2 py-0.5">
        <AppText variant="micro" weight="bold" tabular className="text-ink">
          {score}
        </AppText>
      </View>
      <AppText variant="micro" tabular className="text-muted" style={{ width: 42, textAlign: 'right' }}>
        {shortDate()}
      </AppText>
    </View>
  );
}

/** A team's recent-form block: crest + name header, then its last results. */
function TeamFormSection({ team }: { team: H2H365TeamForm }) {
  if (!team.recent.length) return null;
  return (
    <View>
      <View className="flex-row items-center gap-2 border-y border-glass-border bg-white/5 px-4 py-2">
        <Crest uri={team.crest} size={18} />
        <AppText variant="micro" weight="bold" className="text-ink" numberOfLines={1}>
          {team.name ?? ''}
        </AppText>
      </View>
      {team.recent.map((game, index) => (
        <RecentRow
          key={`${game.date ?? 'r'}-${index}`}
          game={game}
          isLast={index === team.recent.length - 1}
        />
      ))}
    </View>
  );
}

/**
 * Head to Head (وجها لوجه): a glassy card with a W-D-L summary bar (oriented to
 * the current fixture's home team), the recent previous meetings, AND each team's
 * recent form (last matches with a coloured W/D/L badge) — the rich 365scores
 * "وجها لوجه" layout, minus the betting odds. Sourced from 365scores via the
 * `h2h-365` edge function (keyed off the fixture's scores365_game_id). Self-hides
 * entirely when there's no meeting AND no per-team form. Bilingual + RTL.
 */
export function MatchH2H({ fixtureId }: { fixtureId: string }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { data } = useH2H365(fixtureId);

  const meetings = useMemo(() => data?.meetings ?? [], [data?.meetings]);
  const summary = data?.summary ?? null;
  const home = data?.home ?? null;
  const away = data?.away ?? null;
  const hasForm = (home?.recent.length ?? 0) > 0 || (away?.recent.length ?? 0) > 0;

  // Nothing to show without at least one meeting OR some recent form.
  if (meetings.length === 0 && !hasForm) return null;

  return (
    <View className="mb-4 px-4">
      <Card className="overflow-hidden">
        <View className="gap-3 px-4 pt-4">
          <View className="flex-row items-center gap-2">
            <Ionicons name="git-compare-outline" size={16} color={palette.voltBright} />
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('match.h2h365Title')}
            </AppText>
          </View>
          {summary ? (
            <SummaryBar
              homeWins={summary.homeWins}
              draws={summary.draws}
              awayWins={summary.awayWins}
            />
          ) : null}
        </View>

        {meetings.length > 0 ? (
          <View className="mt-3">
            <View className="border-y border-glass-border bg-white/5 px-4 py-2">
              <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
                {t('match.h2h365Recent')}
              </AppText>
            </View>
            {meetings.map((meeting, index) => (
              <MeetingRow
                key={`${meeting.date ?? 'm'}-${index}`}
                meeting={meeting}
                isLast={index === meetings.length - 1}
              />
            ))}
          </View>
        ) : null}

        {hasForm ? (
          <View className="mt-3">
            <View className="border-y border-glass-border bg-white/5 px-4 py-2">
              <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
                {t('match.h2h365Form')}
              </AppText>
            </View>
            {home ? <TeamFormSection team={home} /> : null}
            {away ? <TeamFormSection team={away} /> : null}
          </View>
        ) : null}
      </Card>
    </View>
  );
}
